import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface ReasonCode {
  id: string;
  scope: 'halt' | 'step_failure' | 'human_decline';
  code: string;
  label: string;
  description: string | null;
  requiresNote: boolean;
  applicableStepKinds: string[] | null;
  source: 'system' | 'org';
  active: boolean;
}

export interface ReasonCodeFilters {
  scope: 'halt' | 'step_failure' | 'human_decline';
  orgId?: string;
  includeInactive?: boolean;
}

const KEYS = {
  list: (filters: ReasonCodeFilters) =>
    [
      'reason-codes',
      filters.scope,
      filters.orgId ?? 'system',
      filters.includeInactive ?? false,
    ] as const,
};

function buildListUrl(filters: ReasonCodeFilters): string {
  const params = new URLSearchParams({ scope: filters.scope });
  if (filters.orgId) params.set('org_id', filters.orgId);
  if (filters.includeInactive) params.set('include_inactive', 'true');
  return `/api/workflows/reason-codes?${params.toString()}`;
}

export function useReasonCodes(filters: ReasonCodeFilters) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => apiGet<ReasonCode[]>(buildListUrl(filters)),
  });
}

export function useCreateReasonCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      scope: 'halt' | 'step_failure' | 'human_decline';
      org_id?: string;
      code: string;
      label: string;
      description?: string;
      requiresNote?: boolean;
    }) => apiPost<ReasonCode>('/api/workflows/reason-codes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reason-codes'] }),
  });
}

export function useUpdateReasonCode(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      label?: string;
      description?: string;
      requiresNote?: boolean;
      active?: boolean;
    }) => apiPatch<ReasonCode>(`/api/workflows/reason-codes/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reason-codes'] }),
  });
}

export function useDeactivateReasonCode(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ ok: boolean }>(`/api/workflows/reason-codes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reason-codes'] }),
  });
}

export function useActivateReasonCode(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPatch<ReasonCode>(`/api/workflows/reason-codes/${id}`, { active: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reason-codes'] }),
  });
}
